import { useCallback, useEffect, useState } from 'react'
import { useI18n, formatar } from '../lib/i18n'
import { corDoModo } from '../lib/sinalCores'

/**
 * Gente: onde tem alguém agora, e o que está combinado.
 *
 * É a resposta à pergunta central do hobby, que a interface anterior não
 * respondia — só dava para ver quem estava na SUA frequência. Num app de
 * comunicação isso é o pior padrão possível: quem chega numa faixa vazia
 * conclui que não há ninguém no app inteiro e não volta.
 *
 * A escada de bandas mostra a atividade como PESO visual, não como número: uma
 * banda com gente acende, uma vazia recua. O olho encontra antes de ler.
 */

interface Presence {
  callsign: string
  freq_khz: number
  mode: string
  band_id: string
  transmitting: boolean
}

interface BandRow {
  band_id: string
  low_khz: number
  high_khz: number
  stations: number
  on_air: number
  who: Presence[] | null
}

interface Sked {
  id: string
  host_callsign: string
  title: string
  freq_khz: number
  mode: string
  band_id?: string
  starts_at: string
  note?: string
  participants: string[] | null
  joined: boolean
}

interface SkedRow {
  sked: Sked
  starts_in_min: number
  live: boolean
  mine: boolean
}

export function Gente({
  meuIndicativo,
  freqAtual,
  modoAtual,
  onTune,
}: {
  meuIndicativo: string
  freqAtual: number
  modoAtual: string
  onTune: (khz: number, mode?: string) => void
}) {
  const { t } = useI18n()

  /** Quanto falta, na linguagem de quem precisa decidir se corre para o rádio. */
  const quando = (min: number, live: boolean): string => {
    if (live) return t.gente.agora
    if (min < 1) return t.gente.ja
    if (min < 60) return formatar(t.gente.emMinutos, { n: min })
    const h = Math.round(min / 60)
    if (h < 24) return formatar(t.gente.emHoras, { n: h })
    const d = Math.round(h / 24)
    return formatar(d === 1 ? t.gente.emDia : t.gente.emDias, { n: d })
  }

  const [bands, setBands] = useState<BandRow[]>([])
  const [total, setTotal] = useState(0)
  const [skeds, setSkeds] = useState<SkedRow[]>([])
  const [marcando, setMarcando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(() => {
    fetch('/api/activity')
      .then((r) => r.json())
      .then((d: { bands: BandRow[] | null; total: number }) => {
        setBands(d.bands ?? [])
        setTotal(d.total ?? 0)
      })
      .catch(() => {})
    fetch('/api/skeds')
      .then((r) => r.json())
      .then((d: { skeds: SkedRow[] | null }) => setSkeds(d.skeds ?? []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    carregar()
    // Atividade envelhece rápido: quem estava no ar há um minuto pode não estar
    // mais, e uma lista velha é pior que lista nenhuma.
    const t = setInterval(carregar, 10_000)
    return () => clearInterval(t)
  }, [carregar])

  const presenca = async (id: string, sair: boolean) => {
    await fetch(`/api/skeds/${id}/presenca${sair ? '?sair=1' : ''}`, { method: 'POST' })
    carregar()
  }

  const desmarcar = async (id: string) => {
    await fetch(`/api/skeds/${id}`, { method: 'DELETE' })
    carregar()
  }

  const comGente = bands.filter((b) => b.stations > 0)
  const maisMovimentada = Math.max(1, ...bands.map((b) => b.stations))

  return (
    <div className="gente">
      <header className="secao">
        <h2>{t.gente.noArAgora}</h2>
        <span className="contagem">{total}</span>
      </header>

      {total === 0 ? (
        <p className="vazio">{t.gente.vazio}</p>
      ) : (
        <ul className="escada">
          {comGente.map((b) => (
            <li key={b.band_id}>
              <button
                className="banda-linha"
                onClick={() => {
                  const p = (b.who ?? [])[0]
                  if (p) onTune(p.freq_khz, p.mode)
                }}
              >
                <span className="banda-id">{b.band_id}</span>
                {/* A barra é a informação; o número é a confirmação. */}
                <span className="banda-barra">
                  <i style={{ width: `${(b.stations / maisMovimentada) * 100}%` }} />
                </span>
                <span className="banda-qtd">
                  {b.stations}
                  {b.on_air > 0 && <em className="no-ar">{b.on_air} {t.gente.noAr}</em>}
                </span>
              </button>
              <ul className="quem">
                {(b.who ?? []).map((p) => (
                  <li key={p.callsign + p.freq_khz}>
                    <button onClick={() => onTune(p.freq_khz, p.mode)}>
                      <strong>{p.callsign}</strong>
                      <span className="freq">{(p.freq_khz / 1000).toFixed(3)}</span>
                      <span className="modo" style={{ color: corDoModo(p.mode) }}>{p.mode}</span>
                      {p.transmitting && <span className="pill tx">{t.gente.noArPill}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <header className="secao">
        <h2>{t.gente.encontrosMarcados}</h2>
        <button className="link" onClick={() => setMarcando((v) => !v)}>
          {marcando ? t.gente.cancelar : t.gente.marcar}
        </button>
      </header>

      {marcando && (
        <NovoEncontro
          freqInicial={freqAtual}
          modoInicial={modoAtual}
          onErro={setErro}
          onPronto={() => {
            setMarcando(false)
            carregar()
          }}
        />
      )}
      {erro && <p className="denial">{erro}</p>}

      {skeds.length === 0 && !marcando && (
        <p className="vazio">{t.gente.vazioEncontros}</p>
      )}

      <ul className="encontros">
        {skeds.map(({ sked: k, starts_in_min, live, mine }) => (
          <li key={k.id} className={live ? 'encontro vivo' : 'encontro'}>
            <div className="encontro-topo">
              <strong>{k.title}</strong>
              <span className={live ? 'quando agora' : 'quando'}>
                {quando(starts_in_min, live)}
              </span>
            </div>
            <button className="encontro-freq" onClick={() => onTune(k.freq_khz, k.mode)}>
              {(k.freq_khz / 1000).toFixed(3)} MHz {k.mode}
              {k.band_id && <span className="pill">{k.band_id}</span>}
            </button>
            {k.note && <p className="encontro-recado">{k.note}</p>}
            <p className="encontro-quem">
              {k.host_callsign} {t.gente.convida}{' '}
              {(k.participants ?? []).length === 1
                ? t.gente.ninguemConfirmou
                : `${(k.participants ?? []).length} ${t.gente.confirmados}`}
              {(k.participants ?? []).length > 1 && (
                <span className="lista">{(k.participants ?? []).join(', ')}</span>
              )}
            </p>
            <div className="encontro-acoes">
              {k.host_callsign !== meuIndicativo && (
                <button
                  className={k.joined ? '' : 'primario'}
                  onClick={() => void presenca(k.id, k.joined)}
                >
                  {k.joined ? t.gente.naoVouMais : t.gente.euVou}
                </button>
              )}
              {mine && (
                <button className="link" onClick={() => void desmarcar(k.id)}>
                  {t.gente.desmarcar}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Formulário de encontro, pré-preenchido com onde o operador já está. */
function NovoEncontro({
  freqInicial,
  modoInicial,
  onPronto,
  onErro,
}: {
  freqInicial: number
  modoInicial: string
  onPronto: () => void
  onErro: (e: string | null) => void
}) {
  const { t } = useI18n()
  const [title, setTitle] = useState('')
  const [freq, setFreq] = useState(String(freqInicial))
  const [mode, setMode] = useState(modoInicial || 'FM')
  const [note, setNote] = useState('')
  // Padrão: daqui a uma hora, arredondado. Quem marca encontro pensa em "hoje à
  // noite", não em preencher data.
  const [quandoISO, setQuandoISO] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000)
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0)
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
  })
  const [busy, setBusy] = useState(false)

  const salvar = async () => {
    setBusy(true)
    onErro(null)
    try {
      const r = await fetch('/api/skeds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          freq_khz: Number(freq),
          mode,
          note,
          // O input dá hora local; o servidor guarda em UTC.
          starts_at: new Date(quandoISO).toISOString(),
        }),
      })
      const d = (await r.json()) as { message?: string }
      if (!r.ok) {
        onErro(d.message ?? t.gente.erroMarcar)
        return
      }
      onPronto()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="novo-encontro">
      <label htmlFor="sk-title">{t.gente.sobreOQue}</label>
      <input
        id="sk-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t.gente.placeholderTitulo}
        maxLength={80}
      />

      <div className="linha">
        <div>
          <label htmlFor="sk-freq">{t.gente.frequencia}</label>
          <input id="sk-freq" type="number" value={freq} onChange={(e) => setFreq(e.target.value)} />
        </div>
        <div>
          <label htmlFor="sk-mode">{t.gente.modo}</label>
          <select id="sk-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            {['FM', 'SSB', 'AM', 'CW'].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <label htmlFor="sk-quando">{t.gente.quando}</label>
      <input
        id="sk-quando"
        type="datetime-local"
        value={quandoISO}
        onChange={(e) => setQuandoISO(e.target.value)}
      />

      <label htmlFor="sk-note">{t.gente.recado}</label>
      <input
        id="sk-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t.gente.placeholderRecado}
        maxLength={280}
      />

      <button className="primario" onClick={() => void salvar()} disabled={busy || !title}>
        {busy ? t.gente.marcando : t.gente.marcarEncontro}
      </button>
    </div>
  )
}
